import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { refreshStore } from "../lib/store";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { uniqueCats } from "../lib/cats";
import { ArrowUp, ArrowDown, Save, Star, X, LayoutGrid, Zap, Languages } from "lucide-react";

const move = (arr, i, dir) => {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const next = [...arr];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

export const StorefrontCard = ({ s, setS }) => {
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [tjBusy, setTjBusy] = useState(false);
  const order = s.category_order || [];
  const featured = useMemo(() => s.featured_product_ids || [], [s.featured_product_ids]);
  const addable = useMemo(
    () => products.filter((p) => !featured.includes(p.id)),
    [products, featured]
  );
  useEffect(() => {
    Promise.all([
      api.get("/products", { params: { kind: "grocery" } }),
      api.get("/products", { params: { kind: "menu" } }),
    ]).then(([g, m]) => setProducts([...g.data, ...m.data]));
  }, []);

  const allCats = uniqueCats(products.map((p) => p.category));
  const ordered = [...order.filter((c) => allCats.includes(c)),
                   ...allCats.filter((c) => !order.includes(c))];

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings", {
        category_order: ordered,
        featured_product_ids: featured,
        whatsapp_float_enabled: s.whatsapp_float_enabled !== false,
        whatsapp_number: s.whatsapp_number || "",
        whatsapp_message: s.whatsapp_message || "",
        category_icons: s.category_icons || {},
        flash_enabled: !!s.flash_enabled,
        flash_title: s.flash_title || "",
        flash_subtitle: s.flash_subtitle || "",
        flash_title_ja: s.flash_title_ja || "",
        flash_subtitle_ja: s.flash_subtitle_ja || "",
        flash_code: s.flash_code || "",
        flash_pct: Number(s.flash_pct) || 0,
        flash_ends_at: s.flash_ends_at || "",
      });
      setS(data);
      await refreshStore();
      toast.success("Tampilan halaman depan disimpan");
    } catch (e) {
      toast.error(errMsg(e, "Gagal menyimpan"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="bg-white border border-gray-200 rounded-2xl p-5 space-y-5"
      data-testid="storefront-card"
    >
      <p className="font-display font-semibold flex items-center gap-2">
        <LayoutGrid size={18} className="text-primary" /> Tampilan Halaman Depan
      </p>

      <div className="space-y-2">
        <p className="text-sm font-medium">Urutan grid kategori</p>
        {ordered.length === 0 && (
          <p className="text-xs text-muted-foreground">Belum ada kategori produk.</p>
        )}
        {ordered.map((c, i) => (
          <div
            key={c}
            className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2"
            data-testid={`cat-order-row-${c}`}
          >
            <span className="num text-xs text-muted-foreground w-5">{i + 1}</span>
            <span className="flex-1 text-sm truncate">{c}</span>
            <Button
              size="icon"
              variant="ghost"
              data-testid={`cat-up-${c}`}
              onClick={() => setS({ ...s, category_order: move(ordered, i, -1) })}
            >
              <ArrowUp size={15} />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              data-testid={`cat-down-${c}`}
              onClick={() => setS({ ...s, category_order: move(ordered, i, 1) })}
            >
              <ArrowDown size={15} />
            </Button>
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium">Produk unggulan (grid &ldquo;Pilihan Toko&rdquo;)</p>
        {featured.length === 0 && (
          <p className="text-xs text-muted-foreground">Belum ada produk unggulan.</p>
        )}
        {featured.map((id, i) => {
          const p = products.find((x) => x.id === id);
          return (
            <div
              key={id}
              className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2"
              data-testid={`featured-row-${id}`}
            >
              <span className="num text-xs text-muted-foreground w-5">{i + 1}</span>
              <span className="flex-1 text-sm truncate">{p ? p.name : "(produk terhapus)"}</span>
              <Button
                size="icon"
                variant="ghost"
                data-testid={`featured-up-${id}`}
                onClick={() => setS({ ...s, featured_product_ids: move(featured, i, -1) })}
              >
                <ArrowUp size={15} />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                data-testid={`featured-down-${id}`}
                onClick={() => setS({ ...s, featured_product_ids: move(featured, i, 1) })}
              >
                <ArrowDown size={15} />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                data-testid={`featured-remove-${id}`}
                onClick={() =>
                  setS({ ...s, featured_product_ids: featured.filter((x) => x !== id) })
                }
              >
                <X size={15} className="text-red-500" />
              </Button>
            </div>
          );
        })}
        <select
          data-testid="featured-add-select"
          className="w-full h-11 rounded-xl border border-gray-200 bg-white px-3 text-sm"
          value=""
          onChange={(e) =>
            e.target.value &&
            setS({ ...s, featured_product_ids: [...featured, e.target.value] })
          }
        >
          <option value="">+ Tambah produk unggulan…</option>
          {addable.map((p) => (
            <option key={p.id} value={p.id}>
              {p.kind === "menu" ? "[Resto] " : "[Toko] "}
              {p.name}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-3 border-t border-dashed pt-4">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-medium flex items-center gap-2">
            <Star size={15} className="text-primary" /> Tombol WhatsApp melayang
          </p>
          <Switch
            data-testid="wa-float-switch"
            checked={s.whatsapp_float_enabled !== false}
            onCheckedChange={(v) => setS({ ...s, whatsapp_float_enabled: v })}
          />
        </div>
        <Input
          data-testid="wa-number-input"
          placeholder="+62818776676"
          value={s.whatsapp_number || ""}
          onChange={(e) => setS({ ...s, whatsapp_number: e.target.value })}
        />
        <Input
          data-testid="wa-message-input"
          placeholder="Halo Kumamoto Halal Food, saya ingin bertanya tentang..."
          value={s.whatsapp_message || ""}
          onChange={(e) => setS({ ...s, whatsapp_message: e.target.value })}
        />
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium">Ikon kategori</p>
        <p className="text-xs text-muted-foreground">
          Kosongkan untuk memakai foto produk. Tempel URL gambar/ilustrasi khusus per kategori.
        </p>
        {ordered.map((c) => (
          <div key={`icon-${c}`} className="flex items-center gap-2" data-testid={`cat-icon-row-${c}`}>
            <div className="h-10 w-10 rounded-lg bg-gray-100 overflow-hidden shrink-0">
              {(s.category_icons || {})[c] && (
                <img
                  src={(s.category_icons || {})[c]}
                  alt={c}
                  className="h-full w-full object-cover"
                />
              )}
            </div>
            <span className="w-24 shrink-0 text-xs font-medium truncate">{c}</span>
            <Input
              data-testid={`cat-icon-input-${c}`}
              placeholder={`URL ikon ${c}`}
              value={(s.category_icons || {})[c] || ""}
              onChange={(e) =>
                setS({ ...s, category_icons: { ...(s.category_icons || {}), [c]: e.target.value } })
              }
            />
          </div>
        ))}
      </div>

      <div className="space-y-3 border-t border-dashed pt-4">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-medium flex items-center gap-2">
            <Zap size={15} className="text-primary" /> Diskon Kilat (hitung mundur)
          </p>
          <Switch
            data-testid="flash-enabled-switch"
            checked={!!s.flash_enabled}
            onCheckedChange={(v) => setS({ ...s, flash_enabled: v })}
          />
        </div>
        <Input
          data-testid="flash-title-input"
          placeholder="Judul, mis. Diskon Kilat Hari Ini"
          value={s.flash_title || ""}
          onChange={(e) => setS({ ...s, flash_title: e.target.value })}
        />
        <Input
          data-testid="flash-subtitle-input"
          placeholder="Sub judul, mis. Pakai kode sebelum waktu habis"
          value={s.flash_subtitle || ""}
          onChange={(e) => setS({ ...s, flash_subtitle: e.target.value })}
        />
        <Input
          data-testid="flash-title-ja-input"
          placeholder="日本語の見出し (judul bahasa Jepang)"
          value={s.flash_title_ja || ""}
          onChange={(e) => setS({ ...s, flash_title_ja: e.target.value })}
        />
        <Input
          data-testid="flash-subtitle-ja-input"
          placeholder="日本語のサブ見出し (sub judul bahasa Jepang)"
          value={s.flash_subtitle_ja || ""}
          onChange={(e) => setS({ ...s, flash_subtitle_ja: e.target.value })}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="translate-ja-btn"
          disabled={tjBusy}
          onClick={async () => {
            setTjBusy(true);
            try {
              const { data } = await api.post("/settings/translate-ja", {});
              setS(data);
              await refreshStore();
              toast.success("Teks Jepang diisi otomatis oleh AI");
            } catch (e) {
              toast.error(errMsg(e, "Gagal menerjemahkan"));
            } finally {
              setTjBusy(false);
            }
          }}
        >
          <Languages size={14} className="mr-1" />
          {tjBusy ? "Menerjemahkan…" : "Isi teks Jepang dengan AI"}
        </Button>
        <p className="text-xs text-muted-foreground">
          Tombol AI juga menerjemahkan judul/teks banner promo dan catatan hari tutup. Kolom Jepang
          yang kosong otomatis memakai teks Indonesia.
        </p>
        <div className="grid sm:grid-cols-3 gap-2">
          <Input
            data-testid="flash-code-input"
            placeholder="Kode promo"
            value={s.flash_code || ""}
            onChange={(e) => setS({ ...s, flash_code: e.target.value.toUpperCase() })}
          />
          <Input
            data-testid="flash-pct-input"
            placeholder="Diskon %"
            inputMode="numeric"
            value={s.flash_pct || ""}
            onChange={(e) => setS({ ...s, flash_pct: Number(e.target.value.replace(/[^0-9]/g, "")) })}
          />
          <Input
            data-testid="flash-ends-input"
            type="datetime-local"
            value={(s.flash_ends_at || "").slice(0, 16)}
            onChange={(e) =>
              setS({
                ...s,
                flash_ends_at: e.target.value ? new Date(e.target.value).toISOString() : "",
              })
            }
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Banner otomatis hilang saat waktunya lewat. Kode promo tetap perlu dibuat di halaman Kupon
          Promo agar bisa dipakai di keranjang.
        </p>
      </div>

      <Button data-testid="save-storefront-btn" disabled={busy} onClick={save}>
        <Save size={15} className="mr-1" /> Simpan Tampilan
      </Button>
    </div>
  );
};
